import { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const useInMemoryPagination = ({ data = [], pageSize = 50, resetOnDataChange = true }) => {
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (resetOnDataChange) {
            setCurrentPage(1);
        }
    }, [data.length, resetOnDataChange]);

    const totalPages = useMemo(() => {
        return Math.ceil(data.length / pageSize);
    }, [data.length, pageSize]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return data.slice(startIndex, endIndex);
    }, [data, currentPage, pageSize]);

    const hasNextPage = useMemo(() => {
        return currentPage < totalPages;
    }, [currentPage, totalPages]);

    const goToNextPage = useCallback(() => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }, [totalPages]);

    const goToPreviousPage = useCallback(() => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    }, []);

    const goToFirstPage = useCallback(() => {
        setCurrentPage(1);
    }, []);

    const goToPage = useCallback((page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    }, [totalPages]);

    return {
        paginatedData,
        currentPage,
        totalPages,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        goToFirstPage,
        goToPage,
    };
};

const usePaginationControls = () => {
    const [queryParams, setQueryParams] = useState({ pageIncrement: 0 });
    const [totalPages, setTotalPages] = useState([]);
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);

    const previous = useCallback(() => {
        setQueryParams({
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor,
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setQueryParams({
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const goToFirstPage = useCallback(() => {
        setQueryParams({
            pageIncrement: 0,
            resetPaging: true
        });
    }, []);

    const handleDataReceived = useCallback((response) => {
        setTotalPages(Math.ceil(response.paging.count / response.paging.pageSize));
        setCurrentPage(page => {
            let result;
            if (response.paging.targetPage) {
                result = response.paging.targetPage;
            }
            else if (queryParams.getLatest) {
                result = Math.ceil(response.paging.count / response.paging.pageSize);
            }
            else if (queryParams.resetPaging) {
                result = 1;
            }
            else if (queryParams.refresh) {
                result = page;
            }
            else {
                result = page + queryParams.pageIncrement;
            }
            setHasNextPage(response.paging.count > result * response.paging.pageSize);
            return result;
        });
        setCursors({
            firstCursor: response.paging.firstCursor,
            lastCursor: response.paging.lastCursor
        });
    }, [queryParams]);

    const handleRefreshList = useCallback((params) => {
        setQueryParams(prev => {
            if (params.refresh) {
                return { ...prev, refresh: true }
            }
            else return {
                pageIncrement: 0,
                firstCursor: "",
                lastCursor: "",
                targetId: params?.targetId,
                resetPaging: !!params?.resetPaging,
                getLatest: !!params?.newId,
            }
        });
    }, []);

    return {
        queryParams,
        currentPage,
        totalPages,
        hasNextPage,
        goToFirstPage,
        next,
        previous,
        handleDataReceived,
        handleRefreshList
    }
}

export {
    useQuery,
    usePaginationControls,
    useInMemoryPagination
};

export {
    hasUiPermission,
    getDeploymentPermission,
    useHasPermission,
    useHasAnyPermission,
    usePermissionsInDomain,
    useDeploymentEditPermissions,
    DEPLOYMENT_EDIT_PERMISSIONS
} from "./permissionsHooks";

export {
    useUnlockedDeployments,
    useDeploymentStages
} from "./deploymentLockHooks";

export {
    useInitialBuildTimeConfig,
    useBuildTimeConfig,
    useTemplateConfig,
    useTemplateMetadata
} from "./deploymentConfigHooks";